import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter, HostListener, Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import {PlanningViewSettingsRoutesModel} from "../../../models/planning-view-settings.model";
import * as _ from "lodash";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {PlanningPanelModel} from "../../../models/planning-panel.model";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {PlanningRouteModel} from "../../../models/planning-route.model";
import {PlanningDragAndDropService} from "../../services/planning-drag-and-drop.service";

require('./planning-draft-routes-panel-list.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-routes-panel-list',
  templateUrl: './planning-draft-routes-panel-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftRoutesPanelListComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() routes: PlanningRouteModel[];
  @Input() resources: any[];
  @Input() draft: any;
  @Input() panel: PlanningPanelModel;
  @Input() panels: PlanningPanelModel[];
  @Input() jobs: any[];
  @Input() selectedIds: string[];
  @Input() selectedJobIds: string[];
  @Input() visibleIds: string[];
  @Input() settings: PlanningViewSettingsRoutesModel;
  @Input() itemElementSize: string;
  @Input() scrollOffset: number = 0;
  @Output() onSelectRoutes = new EventEmitter<string[]>();
  @Output() onSelect = new EventEmitter<{ route: PlanningRouteModel, scrollOffset: number }>();
  @Output() onVisibleRoutesChange = new EventEmitter<string[]>();
  @Output() onLockRouteChange = new EventEmitter<{id: string, locked: boolean}>();
  @Output() onChangeRouteResource = new EventEmitter<{routeId: string, event: any}>();
  @Output() onEditResourceAllocation = new EventEmitter<{route: PlanningRouteModel, event: any}>();
  @Output() onAddToRoute = new EventEmitter();
  @Output() onDrillDown = new EventEmitter<{routeId: string, event: any}>();
  @Output() onDispatch = new EventEmitter<string>();
  @Output() onReoptimize = new EventEmitter<string>();
  @Output() onPreAssignAgent = new EventEmitter<string>();
  @Output() onPrint = new EventEmitter<string>();
  @Output() onPrintLoadingSheet = new EventEmitter<string>();
  @Output() onDelete = new EventEmitter<string>();
  @Output() onFeedback = new EventEmitter<{ routeId: string, feedback: string }>();

  private _viewport?: CdkVirtualScrollViewport;

  @ViewChild(CdkVirtualScrollViewport)

  set viewport(viewport: CdkVirtualScrollViewport) {
    this._viewport = viewport;
    if (this._viewport) {
      setTimeout(() => this._viewport.scrollToOffset(this.scrollOffset), 0);
      this._ensureViewportResizeObserver();
    }
  }

  get viewport(): CdkVirtualScrollViewport {
    return this._viewport;
  }

  public connectedPanelIds: string[];
  public selectedKeys: Object;
  public visibleKeys: Object;

  // itemSize is measured from a real rendered item once it's available, then
  // re-measured whenever settings/density change the row's natural height.
  // Initial fallback (120) covers the typical "few columns" baseline.
  public itemSize: number = 120;
  private _measureRafId: number | null = null;
  // public hoveredRouteId?: string;
  public dropAnimations: Array<{x: number, y: number, text: string, id: number, isMultiple: boolean, count: number}> = [];
  public resourceIds: string[] = [];
  public logisticsVehicleNameByResourceId: { [resourceId: string]: string } = {};

  constructor(
    @Inject('confirmDialogService') private confirmDialogService,
    @Inject('planningAlertsService') private planningAlertsService,
    @Inject('planningDialogService') private planningDialogService,
    @Inject('$timeout') private $timeout,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(PlanningDragAndDropService) private planningDragAndDropService: PlanningDragAndDropService) {

  }

  ngOnInit() {

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.resources || changes.routes) {
      const resourceIds = _.map(this.resources, 'id');
      const panelIds = _.map(this.panels, 'id');
      const routeIds = _.map(this.routes, 'id');
      this.connectedPanelIds = _.uniq(_.concat(panelIds || [], resourceIds || [], routeIds || []));
    }
    if (changes.resources || changes.routes) {
      this.logisticsVehicleNameByResourceId = _.mapValues(_.keyBy(this.resources, 'id'), 'mainLogisticsVehicleReference.name');
    }
    if (changes.selectedIds) {
      this.selectedKeys = {};
      _.forEach(this.selectedIds, (id) => {
        this.selectedKeys[id] = true;
      });
    }
    if (changes.visibleIds) {
      this.visibleKeys = {};
      _.forEach(this.visibleIds, (id) => {
        this.visibleKeys[id] = true;
      });
    }
    if (changes.settings || changes.itemElementSize || changes.routes) {
      this._scheduleItemMeasure();
    }
    this.resourceIds = this.resources.map(res => res.id);
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.viewport?.checkViewportSize();
  }

  private viewportResizeObserver?: ResizeObserver;

  ngAfterViewInit() {
    this._ensureViewportResizeObserver();
    this._scheduleItemMeasure();
  }

  private _scheduleItemMeasure(): void {
    if (this._measureRafId !== null) return;
    this._measureRafId = requestAnimationFrame(() => {
      this._measureRafId = null;
      this._measureItemSize();
    });
  }

  private _measureItemSize(): void {
    if (!this.viewport) return;
    const wrapper = this.viewport.elementRef.nativeElement
      .querySelector('.cdk-virtual-scroll-content-wrapper') as HTMLElement | null;
    const first = wrapper?.firstElementChild as HTMLElement | null;
    if (!first) return;
    const h = first.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - this.itemSize) > 1) {
      this.itemSize = Math.round(h);
      this.cdr.detectChanges();
      this.viewport.checkViewportSize();
    }
  }

  private _ensureViewportResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.viewportResizeObserver = new ResizeObserver(() => {
      if (this.viewport && this.routes?.length > 0) {
        this.viewport.checkViewportSize();
      }
    });
    // Need a slight delay to ensure viewport element is available since it's under *ngIf
    setTimeout(() => {
      if (this.viewport?.elementRef?.nativeElement) {
        this.viewportResizeObserver?.observe(this.viewport.elementRef.nativeElement);
      }
    }, 0);
  }

  ngOnDestroy() {
    if (this.viewportResizeObserver) {
      this.viewportResizeObserver.disconnect();
      this.viewportResizeObserver = undefined;
    }
    if (this._measureRafId !== null) {
      cancelAnimationFrame(this._measureRafId);
      this._measureRafId = null;
    }
  }

  onToggleSelectedRoute(id, isSelected: boolean) {
    this.selectedKeys[id] = isSelected;
    const selectedIds = _.map(this.selectedKeys, (value, key) => {
      if (value) return key;
    });

    this.onSelectRoutes.emit(_.compact(selectedIds));
  }

  onToggleVisibleRoute(id, isVisible: boolean) {
    this.visibleKeys[id] = isVisible;
    const visibleIds = _.map(this.visibleKeys, (value, key) => {
      if (value) return key;
    });

    this.onVisibleRoutesChange.emit(_.compact(visibleIds));
  }

  // onDropEnter(routeId: string) {
  //   this.hoveredRouteId = routeId;
  // }
  //
  // onDropExit(routeId: string) {
  //   if (this.hoveredRouteId === routeId) {
  //     this.hoveredRouteId = null;
  //   }
  // }

  onDrop(route: any, event: CdkDragDrop<string[]>) {
    this.planningDragAndDropService.onDropRoutes(
      route,
      this.draft?.settings.optimizeDispatchedRoutes ?? false,
      event,
      this.selectedJobIds,
      this.jobs,
      this.panels,
      this.showDropAnimation.bind(this)
    ).then((payload) => {
      if (payload) {
        this.onAddToRoute.emit(payload);
      }
    });
  }

  public showDropAnimation(x: number, y: number, count: number, isMultiple: boolean): void {
    const animationId = Date.now();

    this.dropAnimations.push({
      x: x,
      y: y,
      text: isMultiple ? `${count} Jobs` : 'Job',
      id: animationId,
      isMultiple: isMultiple,
      count: count
    });

    this.cdr.detectChanges();

    this.$timeout(() => {
      this.dropAnimations = this.dropAnimations.filter(a => a.id !== animationId);
      this.cdr.detectChanges();
    }, 600);
  }

  trackByRouteId(_: number, item: { id: string | number }) {
    return item.id;
  }
}
